import React, { createContext, useCallback, useEffect, useState } from "react";

interface WellOption {
    id: string;
    name: string;
    value: string;
    customerId?: string | null;
}

interface WellsContextValue {
    wells: WellOption[];
    loading: boolean;
    refresh: (includeCustomer?: boolean) => Promise<void>;
    ensureAssetPresent: (assetId: string) => Promise<WellOption | undefined>;
    getById: (id: string) => WellOption | undefined;
    setWellsData: (wells: any[]) => void;
}

export const WellsContext = createContext<WellsContextValue>({
    wells: [],
    loading: true,
    refresh: async () => { },
    ensureAssetPresent: async () => undefined,
    getById: () => undefined,

    // NEW
    setWellsData: () => { }
});

const CACHE_KEY = "wellsCache";
const CACHE_TTL_MS = 5 * 60 * 1000;

export const WellsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const [wells, setWells] = useState<WellOption[]>([]);
    const [loading, setLoading] = useState(true);

    const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";

    const getBrowserTimeZone = () =>
        Intl.DateTimeFormat().resolvedOptions().timeZone;

    const readCache = (): WellOption[] | null => {
        try {
            const raw = sessionStorage.getItem(CACHE_KEY);
            if (!raw) return null;

            const parsed = JSON.parse(raw);

            if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) {
                return null;
            }

            return parsed.data || null;
        } catch {
            return null;
        }
    };

    const setWellsData = useCallback((rawWells: any[]) => {

        const mapped: WellOption[] = (rawWells || []).map((w: any) => ({
            id: w.assetId,
            name: w.wellName,
            value: w.wellName,
            customerId: w.customerId
        }));

        // remove duplicates
        const uniqueMap = new Map();

        mapped.forEach((item) => {
            uniqueMap.set(String(item.id), item);
        });

        const uniqueWells = Array.from(uniqueMap.values());

        // sort
        uniqueWells.sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
        );

        // UPDATE STATE
        setWells(uniqueWells);

        // FORCE CACHE UPDATE
        try {
            sessionStorage.setItem(
                CACHE_KEY,
                JSON.stringify({
                    ts: Date.now(),
                    data: uniqueWells
                })
            );
        } catch (e) {
            console.error("Failed to update wells cache", e);
        }

    }, []);

    const fetchWells = useCallback(async (includeCustomer = true) => {

        setLoading(true);

        const userTimeZone = getBrowserTimeZone();

        const stateNodString = localStorage.getItem("selectedNode");
        const selectCustomerId = localStorage.getItem("selectedCustomers");

        const stateNod = stateNodString
            ? JSON.parse(stateNodString)
            : null;

        const customerIds = selectCustomerId
            ? JSON.parse(selectCustomerId)
            : [];

        const params = new URLSearchParams();

        if (userTimeZone) {
            params.append("timeZoneId", String(userTimeZone));
        }

        if (
            stateNod?.id !== undefined &&
            stateNod?.id !== null &&
            stateNod?.id !== "#"
        ) {
            params.append("nodeId", String(stateNod.id));
        }

        if (
            includeCustomer &&
            customerIds &&
            customerIds.length
        ) {
            params.append("customerId", String(customerIds));
        }

        const queryString = params.toString();

        const trimmedBase = String(baseUrl).replace(/\/$/, "");

        const apiUrl =
            `${trimmedBase}/Wells/GetALLWells${queryString ? `?${queryString}` : ""}`;

        const headers = {
            Authorization: `Bearer ${localStorage.getItem("jwtAccessToken")}`,
            "Content-Type": "application/json",
        };

        try {

            const response = await fetch(apiUrl, { headers });

            if (response.status === 401) {
                setWells([]);
                setLoading(false);
                return;
            }

            if (!response.ok) {
                setWells([]);
                setLoading(false);
                return;
            }

            const data = await response.json();

            const rawWells = data[0]?.wells || [];

            // UPDATED
            setWellsData(rawWells);

        } catch (err) {

            console.error("Error fetching wells:", err);
            setWells([]);

        } finally {

            setLoading(false);
        }

    }, [baseUrl, setWellsData]);

    const refresh = useCallback(async (includeCustomer = true) => {
        await fetchWells(includeCustomer);
    }, [fetchWells]);

    const ensureAssetPresent = useCallback(async (assetId: string) => {

        if (!assetId) return undefined;

        const existing = wells.find(
            (w) => String(w.id) === String(assetId)
        );

        if (existing) return existing;

        const cached = readCache();

        if (cached) {

            const found = cached.find(
                (w) => String(w.id) === String(assetId)
            );

            if (found) {

                const mergedIds = new Set(
                    wells.map((w) => String(w.id))
                );

                const merged = [...wells];

                cached.forEach((c) => {
                    if (!mergedIds.has(String(c.id))) {
                        merged.push(c);
                    }
                });

                merged.sort((a, b) =>
                    String(a.name).localeCompare(String(b.name))
                );

                setWells(merged);

                return found;
            }
        }

        await fetchWells(false);

        const postCache = readCache();

        if (postCache) {
            return postCache.find(
                (w) => String(w.id) === String(assetId)
            );
        }

        return undefined;

    }, [fetchWells, wells]);

    const getById = useCallback((id: string) => {
        return wells.find(
            (w) => String(w.id) === String(id)
        );
    }, [wells]);

    useEffect(() => {

        const cached = readCache();

        if (cached) {

            setWells(cached);
            setLoading(false);

        } else {

            fetchWells(true);
        }

    }, [fetchWells]);

    useEffect(() => {
        const handleCacheUpdate = (event: any) => {
            const updatedData = event.detail?.data || [];
            setWells(updatedData);
            setLoading(false);
        };

        window.addEventListener(
            "wells-cache-updated",
            handleCacheUpdate
        );

        return () => {
            window.removeEventListener(
                "wells-cache-updated",
                handleCacheUpdate
            );
        };
    }, []);

    return (
        <WellsContext.Provider
            value={{
                wells,
                loading,
                refresh,
                ensureAssetPresent,
                getById,

                // NEW
                setWellsData
            }}
        >
            {children}
        </WellsContext.Provider>
    );
};

export default WellsProvider;